import { ChangeEvent } from "react";
import { FormikConfig, FormikErrors, getIn, useFormik } from "formik";
import * as store from "../../framework/customStore";
import * as StoreActions from "../../models/store/storeActions";
import * as storeActions from "../../models/store/storeActions";
import * as baseService from "../../services/baseService";
import { ConfirmationDialogType } from "../../models/store/storeTypes";

export const defaultFormikConfig: Partial<FormikConfig<any>> = {
    validateOnBlur: true,
    validateOnMount: true,
};

export interface FormikFieldProps<T = string | string[]> {
    name: string;
    value: T;
    onChange: (e: any) => void;
    onBlur: (e: any) => void;
    onFocus?: (e: ChangeEvent) => void;
    error: boolean;
    helperText?: string;
}
export interface FormikValidationProps {
    name: string;
    error: boolean;
    helperText: string;
    onBlur: (e: any) => void;
}

export type FormikForm<T = any> = ReturnType<typeof useFormik<T>>;

// Used for formik form fields with validations. If the field has no validation rules, set only "name" instead.
export const formikFieldProps = <T = string | string []>(formik: FormikForm, field: string): FormikFieldProps<T> => ({
    name: field,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    value: getIn(formik.values, field),
    onChange: (val) => {
        if (val?.target) {
            // argument propably an event, let formik handle it.
            formik.handleChange(val);
        } else {
            void formik.setFieldValue(field, val);
        }
    },
    onBlur: formik.handleBlur,
    error: getIn(formik.touched, field) && Boolean(getIn(formik.errors, field)),
    helperText: getIn(formik.touched, field) && getIn(formik.errors, field) as string,
});

export const formikValidationProps = (formik: FormikForm, field: string): FormikValidationProps => ({
    name: field,
    error: getIn(formik.touched, field) && Boolean(getIn(formik.errors, field)),
    helperText: getIn(formik.touched, field) && getIn(formik.errors, field) as string,
    onBlur: formik.handleBlur
});

const getNestedErrors = (e: FormikErrors<any>): string[] | string =>
    typeof e === "object"
        ? Object.values(getNestedErrors(e)).flat()
        : (e as string);

export const formikErrors = (formik: FormikForm): string[] =>
    Object.values(formik.errors).flatMap(getNestedErrors);

// Replace number surrounded by curly braces in string with corresponding value. Similar to string.Format in C#.
export const formatString = (text: string, ...replacingValues: (string | number)[]) => {
    replacingValues.forEach((value, i) => {
        text = text.replace(`{${i}}`, value.toString());
    });
    return text;
};

// Makes it easier to show error messages returned from APIs.
export const showApiError = (error, fallback?: string) => {
    store.customStore.dispatch(
        StoreActions.showErrorMessage(
            baseService.getErrorMessageFromError(error, fallback)
        )
    );
};

export const showApiSuccess = (msg: string) => {
    store.customStore.dispatch(StoreActions.showSuccessMessage(msg));
};

export const showConfirm = (title: string, onYes: () => void, onNo?: () => void, onCancel?: () => void) => {
    store.customStore.dispatch(storeActions.setConfirmation(
        ConfirmationDialogType.Warning,
        title,
        null, // Text is always empty to keep this as simple as possible. Set title instead.
        onYes,
        onNo ?? null,
        onCancel ?? (() => null),
    ));
};
